import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useHoldGuard } from '../schedule/holdGuard';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * Café Wizard — "pick a template → tap play" flow (café accounts only).
 *
 * Content templates (☕ Café Vibes / 🎬 Silent Display) hit the net-new
 * composed backend endpoint POST /api/cafe/wizard/vibes, which builds a normal
 * re-playable Show behind the scenes (layers / mute / publish never surface).
 *
 * 📢 Quick Promo reuses the existing Happy Hour engine (see queries/happyHour).
 * 🏟️ Match templates reuse the Scenes engine (added in the next slice).
 */

// ── Wizard context ───────────────────────────────────────────────────────────
// One fresh call telling a wizard how to run its "Screens" step:
//   { hasPtnHardware, screens:[{id,name,serial_name}], defaultDevices:[id,...] }
// Simple (no PTN) → skip the picker, auto-assign defaultDevices; Full → picker.
export const useWizardContext = (enabled = true) =>
  useQuery('cafeWizardContext', async () => (await axiosInstance.get('/cafe/wizard/context')).data,
    { enabled, staleTime: 10_000 });

// ── Save step occupancy ──────────────────────────────────────────────────────
// { vibes_morning: {state:'free'|'busy'|'mixed'|'unknown', name, devices:[{device_id,device_name,occupied,name}]},
//   vibes_afternoon: {...}, vibes_evening: {...} }
// ⚠️ PER DEVICE, NOT VENUE-WIDE (2026-09-05) — occupancy became per-device
// underneath when venue_show_slots did, so a caller that omits deviceIds
// gets 'unknown' for every slot rather than a guessed answer (see the
// backend's own docblock). deviceIds is IN THE QUERY KEY so a change in the
// wizard's Screens step selection refetches occupancy for the NEW
// selection, not a stale one cached under the old device set.
// staleTime:0 — occupancy must be fresh every time the save step is reached,
// not cached across a session where the café might have re-run a slot since.
export const useCafeWizardSlots = (deviceIds = [], enabled = true) =>
  useQuery(
    ['cafeWizardSlots', ...deviceIds],
    async () => (await axiosInstance.get('/cafe/wizard/slots', { params: { device_ids: deviceIds } })).data?.data,
    { enabled: enabled && deviceIds.length > 0, staleTime: 0 },
  );

// ── Media pickers ──────────────────────────────────────────────────────────
// Flat listing of the WHOLE library (every folder + files that also live in
// playlists), unpaginated — GET /cafe/wizard/media?kind=. The gallery's
// /media?layer= endpoint is folder-scoped + paginated, which hid nested files.

const fetchCafeMedia = async (kind) => {
  const { data } = await axiosInstance.get(`/cafe/wizard/media?kind=${kind}`);
  return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
};

/**
 * Flat media list keyed by kind ('visual' = photos/videos, 'music' = audio,
 * 'youtube' = saved YouTube links). Whole library, newest first, unpaginated —
 * the picker does search / tab-filter / pagination client-side (instant).
 */
export const useCafeMedia = (kind = 'visual', enabled = true) =>
  useQuery(['cafeMedia', kind], () => fetchCafeMedia(kind), { enabled, staleTime: 5_000 });

// ── Match Mode / Match + Music ───────────────────────────────────────────────
export const useStartCafeMatch = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    (payload) => axiosInstance.post('/cafe/wizard/match', payload),
    {
      onSuccess: () => {
        qc.invalidateQueries('getGroupedDevices');
        qc.invalidateQueries('getDevicesList');
        qc.invalidateQueries('scenes');
      },
      onError: (err) =>
        showToast('error', 'Match', err.response?.data?.message ?? 'Something went wrong.'),
    },
  );
};

// ── Café Vibes / Silent Display ──────────────────────────────────────────────
export const useStartCafeVibes = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  const ensureHold = useHoldGuard(); // schedule v4: asks first when a chosen screen is inside a schedule window
  return useMutation(
    async (payload) => {
      await ensureHold({ mode: 'screens', ids: payload.device_ids || [] });
      return axiosInstance.post('/cafe/wizard/vibes', payload);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('getGroupedDevices');
        qc.invalidateQueries('getDevicesList');
        // A run may have just occupied/replaced a daypart slot — the save
        // step's occupancy must be fresh on the next visit, not cached.
        qc.invalidateQueries('cafeWizardSlots');
      },
      onError: (err) => {
        if (err?.holdCancelled) return;
        // a question, not a failure: the wizard shows the scope choice itself
        if (err?.response?.status === 409 && err?.response?.data?.message === 'needs_scope_choice') return;
        showToast('error', 'Café', err.response?.data?.message ?? 'Something went wrong.');
      },
    },
  );
};
